import React from "react";
import {
    Accordion,
    Button,
    Card,
    Flex,
    Heading,
    Icon,
    Notification,
    StepByStep,
    Text,
    VisuallyHidden,
} from "@telia/teddy";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../models";
import type { TeliaPlaySubscription } from "../../../../../models/state/teliaPlay";
import ReceiptOrderDetailsCard from "../../../checkout/views/Receipt/ReceiptOrderDetailsCard";
import { FAQ } from "../../../components/components/molecules/FAQ/FAQ";
import { saveTeliaPlaySummaryToPdf } from "../../../../utils/saveToPdf";
import * as CUSTOMER_INFO_SELECTORS from "../../../checkout/state/selectors/customerInfoSelectors";
import TEXT from "../../text-consts";
import "./Receipt.less";

const hasDestination = (url: string) => Boolean(url && url !== "#");

export const getIncludedPoints = (subscription?: TeliaPlaySubscription | null): number | undefined => {
    const points = Number.parseInt(subscription?.configuration?.tv ?? "", 10);
    return Number.isNaN(points) ? undefined : points;
};

const formatMonthlyPrice = (subscription: TeliaPlaySubscription) =>
    `${Number(subscription.monthlyPrice.regularPrice)},-/md.`;

const OrderAccordion: React.FC<{ subscription: TeliaPlaySubscription }> = ({ subscription }) => (
    <Accordion type="multiple" className="telia-play-receipt__order" noborder>
        <Accordion.Item value="order">
            <Accordion.Header>
                <Accordion.Trigger className="telia-play-receipt__order-trigger">
                    <Flex align="center" gapX="150">
                        <Icon size="md" name="cart" aria-hidden="true" focusable="false" />
                        {TEXT.RECEIPT.SHOW_ORDER}
                    </Flex>
                </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="telia-play-receipt__order-content">
                <div className="telia-play-receipt__order-body">
                    <div className="telia-play-receipt__product-info">
                        <div>
                            <Heading className="telia-play-receipt__product-heading" as="h3" variant="subsection-100">
                                {subscription.subscriptionName}
                            </Heading>
                            <Text className="telia-play-receipt__ingress">{subscription.subscriptionShortname}</Text>
                        </div>
                        <Text className="telia-play-receipt__product-price" variant="paragraph-100-bold">
                            {formatMonthlyPrice(subscription)}
                        </Text>
                    </div>
                    <div className="telia-play-receipt__price-container">
                        <Text variant="paragraph-100-bold">{TEXT.RECEIPT.PER_MONTH}</Text>
                        <Text className="telia-play-receipt__total-price" variant="paragraph-100-bold">
                            {formatMonthlyPrice(subscription)}
                        </Text>
                    </div>
                </div>
            </Accordion.Content>
        </Accordion.Item>
    </Accordion>
);

const GetStartedSteps: React.FC<{ points?: number }> = ({ points }) => (
    <section className="telia-play-receipt__get-started" aria-labelledby="telia-play-receipt-get-started">
        <Heading id="telia-play-receipt-get-started" as="h2" variant="title-300" mb="300">
            {TEXT.RECEIPT.GET_STARTED.HEADING}
        </Heading>
        <StepByStep size="md" standalone>
            <StepByStep.Step>
                <StepByStep.Description
                    heading={TEXT.RECEIPT.GET_STARTED.DOWNLOAD_HEADING}
                    icon={<Icon name="smartphone" aria-hidden="true" focusable="false" />}
                >
                    <Text>{TEXT.RECEIPT.GET_STARTED.DOWNLOAD_TEXT}</Text>
                    {(hasDestination(TEXT.LINKS.GOOGLE_PLAY) || hasDestination(TEXT.LINKS.APP_STORE)) && (
                        <div className="telia-play-receipt__app-links">
                            {hasDestination(TEXT.LINKS.GOOGLE_PLAY) && (
                                <a
                                    href={TEXT.LINKS.GOOGLE_PLAY}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Last ned Telia Play i Google Play (åpnes i nytt vindu)"
                                >
                                    <img
                                        alt="Google Play"
                                        src="/shop/assets/img-static/campaign/google-play-button.svg"
                                    />
                                </a>
                            )}
                            {hasDestination(TEXT.LINKS.APP_STORE) && (
                                <a
                                    href={TEXT.LINKS.APP_STORE}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Last ned Telia Play i App Store (åpnes i nytt vindu)"
                                >
                                    <img alt="App Store" src="/shop/assets/img-static/campaign/app-store-button.svg" />
                                </a>
                            )}
                        </div>
                    )}
                </StepByStep.Description>
            </StepByStep.Step>
            <StepByStep.Step>
                <StepByStep.Description
                    heading={TEXT.RECEIPT.GET_STARTED.LOGIN_HEADING}
                    icon={<Icon name="end-user" aria-hidden="true" focusable="false" />}
                >
                    <Text>{TEXT.RECEIPT.GET_STARTED.LOGIN_TEXT}</Text>
                    {hasDestination(TEXT.LINKS.CREATE_PROFILE) && (
                        <Button className="telia-play-receipt__create-profile" variant="secondary" asChild>
                            <a href={TEXT.LINKS.CREATE_PROFILE} target="_blank" rel="noopener noreferrer">
                                {TEXT.RECEIPT.GET_STARTED.CREATE_PROFILE}
                                <Icon name="external" aria-hidden="true" focusable="false" />
                                <VisuallyHidden>(åpnes i nytt vindu)</VisuallyHidden>
                            </a>
                        </Button>
                    )}
                </StepByStep.Description>
            </StepByStep.Step>
            <StepByStep.Step>
                <StepByStep.Description
                    heading={TEXT.RECEIPT.GET_STARTED.CHOOSE_CONTENT_HEADING}
                    icon={<Icon name="star" aria-hidden="true" focusable="false" />}
                >
                    <Text>{TEXT.RECEIPT.GET_STARTED.CHOOSE_CONTENT_TEXT(points)}</Text>
                </StepByStep.Description>
            </StepByStep.Step>
        </StepByStep>
    </section>
);

const ChangeSubscriptionCard: React.FC = () => (
    <Card className="telia-play-receipt__change-subscription" variant="white" layout="navigation-horizontal-small">
        <Card.Illustration>
            <Icon name="switch-arrows" size="lg" aria-hidden="true" focusable="false" />
        </Card.Illustration>
        <Card.Heading as="h2">{TEXT.RECEIPT.CHANGE_SUBSCRIPTION.HEADING}</Card.Heading>
        <Card.Content>
            <Text>{TEXT.RECEIPT.CHANGE_SUBSCRIPTION.TEXT}</Text>
        </Card.Content>
        <Card.Button asChild iconOnly aria-label={`${TEXT.RECEIPT.CHANGE_SUBSCRIPTION.HEADING} på Min Side`}>
            <a href={TEXT.LINKS.MIN_SIDE}>
                <Icon name="arrow-right" aria-hidden="true" focusable="false" />
            </a>
        </Card.Button>
    </Card>
);

const TeliaPlayReceipt: React.FC = () => {
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [downloadError, setDownloadError] = React.useState(false);
    const orderId = useSelector((state: RootState) => state.general?.orderId);
    const email = useSelector((state: RootState) => state.teliaPlay?.personalDetails?.email);
    const selectedSubscription = useSelector((state: RootState) => state.teliaPlay?.selectedSubscription);
    const legalOwnerFullName = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerFullName(state));
    const legalOwnerAddress = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerAddress(state));
    const legalOwnerCity = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerFullCity(state));
    const points = getIncludedPoints(selectedSubscription);

    const downloadReceipt = async () => {
        if (!selectedSubscription) return;
        setIsDownloading(true);
        setDownloadError(false);
        try {
            await saveTeliaPlaySummaryToPdf(selectedSubscription, {
                name: legalOwnerFullName,
                address: legalOwnerAddress,
                city: legalOwnerCity,
            });
        } catch {
            setDownloadError(true);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="telia-play-receipt">
            <div className="telia-play-receipt__confirmation">
                <Icon
                    className="telia-play-receipt__check-icon"
                    name="check-circle"
                    size="xl"
                    aria-hidden="true"
                    focusable="false"
                />
                <Heading as="h1" variant="title-400" className="telia-play-receipt__heading">
                    {TEXT.RECEIPT.HEADING}
                </Heading>
                {email && (
                    <Text className="telia-play-receipt__confirmation-text" data-di-mask>
                        {TEXT.RECEIPT.ORDER_CONFIRMATION_SENT_TO} {email}
                    </Text>
                )}
                {selectedSubscription && (
                    <Button
                        className="telia-play-receipt__download"
                        variant="text"
                        type="button"
                        disabled={isDownloading}
                        aria-busy={isDownloading}
                        onClick={downloadReceipt}
                    >
                        {isDownloading ? TEXT.RECEIPT.DOWNLOADING_RECEIPT : TEXT.RECEIPT.DOWNLOAD_RECEIPT}
                    </Button>
                )}
                {downloadError && (
                    <div role="alert">
                        <Notification variant="error" type="tile">
                            <Notification.Icon />
                            <Notification.Text>{TEXT.RECEIPT.DOWNLOAD_RECEIPT_ERROR}</Notification.Text>
                        </Notification>
                    </div>
                )}
            </div>
            <div className="telia-play-receipt__content">
                {selectedSubscription && <OrderAccordion subscription={selectedSubscription} />}
                <Notification className="telia-play-receipt__email-info" variant="gray" type="tile">
                    <Notification.Icon name="email" />
                    <Notification.Text>{TEXT.RECEIPT.CHECK_EMAIL}</Notification.Text>
                </Notification>
                <GetStartedSteps points={points} />
                {orderId && (
                    <ReceiptOrderDetailsCard
                        orderId={orderId}
                        deliveryTitle=""
                        deliveryDescription=""
                        isDigitalDelivery
                    >
                        {selectedSubscription && (
                            <>
                                <Heading mb="50" asChild variant="title-100">
                                    <span>{TEXT.RECEIPT.SUBSCRIPTION}:</span>
                                </Heading>
                                <Text>
                                    {points
                                        ? `${selectedSubscription.subscriptionName}, ${TEXT.RECEIPT.POINTS(points)}`
                                        : selectedSubscription.subscriptionName}
                                </Text>
                            </>
                        )}
                    </ReceiptOrderDetailsCard>
                )}
                <ChangeSubscriptionCard />
                <div className="telia-play-receipt__useful-info">
                    <FAQ title={TEXT.RECEIPT.USEFUL_INFO.HEADING} items={TEXT.RECEIPT.USEFUL_INFO.ITEMS} />
                </div>
            </div>
        </div>
    );
};

export default TeliaPlayReceipt;
